import {ApolloProvider} from '@apollo/client';
import React, {useCallback} from 'react';
import {HashRouter as Router, Switch, Route, useHistory, Redirect, Prompt} from 'react-router-dom';
import styled from 'styled-components';
import {ErrorBoundary, type ErrorBoundaryPropsWithComponent, type FallbackProps} from 'react-error-boundary';
import i18next from 'i18next';
import {filter, first} from 'rxjs/operators';

import {SWRConfig} from 'web-app/util/swr';
import {getApolloClient} from 'web-app/api/clients/famly-app';
import APIHelper from 'web-app/services/api-helper';
import LocalStorageService from 'web-app/services/local-storage';
import {loadScript} from 'web-app/util/load-script';
import PlatformHelper from 'web-app/helpers/platform-helper';
import * as Killswitch from 'web-app/services/killswitch';
import {useSetupMobileApp} from 'web-app/services/capacitor-lifecycle';
import {getWhitelabel, getWhitelabelName} from 'web-app/shared/white-labeling-helpers';
import {Whitelabel} from 'web-app/shared/white-labeling-constants';
import {useGetCurrentPageNavigationLinkId} from 'web-app/react/navigation-v2/hooks';
import {navigationContext} from 'web-app/react/contexts/navigation';
import {ModalContext} from 'web-app/react/modal-context/modal-context';
import {captureException} from 'web-app/services/sentry';
import {DialogRenderer} from 'web-app/react/dialog/dialog';
import {ButtonAppearance} from 'web-app/react/components/button-v2';
import LegacyModalStyle from 'web-app/react/global-styles/legacy-modal-style';
import GlobalPrintHeader from 'web-app/react/global-print-header';
import {ReactSelectGlobalStyling} from 'web-app/react/react-select-styles';
import GlobalEvent from 'web-app/react/global-event/global-event';
import DialogComposer from 'web-app/react/dialog/dialog-composer';
import GlobalStyleSplitNavigation from 'web-app/react/global-styles/split-navigation';
import GlobalStyleLegacyTable from 'web-app/react/global-styles/legacy-table-style';
import GlobalStyleSelect from 'web-app/react/global-styles/select-style';
import GlobalStyleTooltip from 'web-app/react/global-styles/tooltip-style';
import GlobalStyleCSSVariables from 'web-app/react/global-styles/global-css-variables';
import {GlobalStyleModernFamly} from 'web-app/react/global-styles/modern-famly';
import setupMenuHide from 'web-app/setup/menu-hide-setup';
import * as SessionHelpers from 'web-app/react/session/helpers';
import generateUUID from 'web-app/util/uuid';
import getInstallationId from 'web-app/services/installation-id-manager';
import {configureSentryScope} from 'web-app/shared/sentry-setup';
import {getConsumer} from 'web-app/util/famlytics/event-consumer';
import RESTClient from 'web-app/api/clients/rest-client-famly-app';
import {APIErrorCode} from 'web-app/api/types';
import {hasValue, type InferProps} from 'web-app/util/typescript';
import {getNavigationLinkDetails} from 'web-app/react/navigation-v2/config/navigation-link';
import * as LoginHelpers from 'web-app/react/login/helpers';
import {DialogChoice, showDialog} from 'web-app/react/dialog';
import {NavigationBlockProvider, type BlockNavigationPromptPayload} from 'web-app/react/hooks/use-block-navigation';
import {extractPathFromHash} from 'web-app/util/url';
import Spinner from 'web-app/react/components/loading/spinner/spinner';
import {SWRConfigValue} from 'web-app/shared/swr-config-value';

import RouteConfig, {generatePath, type PathGenerationArgs, type RouteConfigPath} from './route-config';
import {buildNavigationFunctionReplacement} from './helpers';
import {withAccessTokenResolved$, withApplicationReady$} from './application-route-helpers';
import {GlobalBeamerStyles} from '../global-styles/beamer-style';
import {getFbLoginState} from '../settings/content/feature-settings/communication/news-feed/helpers';

const Welcome = React.lazy(() => import('web-app/react/routes/welcome/welcome'));
const RoleInvitationRoute = React.lazy(() => import('web-app/react/routes/role-invitation'));
const ConnectTokenSent = React.lazy(() => import('web-app/react/login/connect-token-sent'));
const Signup = React.lazy(() => import('web-app/react/routes/signup/signup'));
const MeetingLeft = React.lazy(() => import('web-app/react/famly-live/meeting-left'));
const ServersUnavailable = React.lazy(() => import('web-app/react/servers-unavailable/servers-unavailable'));
const Debugger = React.lazy(() =>
    import('web-app/react/routes/debugger/debugger').then(module => ({default: module.Debugger})),
);
const Icons = React.lazy(() => import('web-app/react/routes/icons/icons'));
const ContextChallenge = React.lazy(() => import('web-app/react/routes/context-challenge/context-challenge'));
const TestLogin = React.lazy(() => import('web-app/react/routes/test-login/test-login'));
const ErrorTest = React.lazy(() => import('web-app/react/routes/error-test/error-test'));
const ErrorLog = React.lazy(() =>
    import('web-app/react/routes/error-log/error-log').then(module => ({default: module.ErrorLog})),
);
const InquiryForm = React.lazy(() => import('web-app/react/routes/inquiry-form/inquiry-form'));
const OneTimeLogin = React.lazy(() => import('web-app/react/routes/one-time-login/one-time-login'));
const FamlyLiveJoinMeeting = React.lazy(
    () => import('web-app/react/routes/famly-live-join-meeting/famly-live-join-meeting'),
);
const ForgotPassword = React.lazy(() => import('web-app/react/routes/login/forgot-password'));
const ResetPassword = React.lazy(() => import('web-app/react/routes/login/reset-password'));
const ConfirmEmail = React.lazy(() => import('web-app/react/routes/confirm-email/confirm-email'));
const NewAccount = React.lazy(() => import('web-app/react/routes/login/new-account'));
const TooManyFailedLoginAttempts = React.lazy(
    () => import('web-app/react/routes/login/too-many-failed-login-attempts'),
);
const Contract = React.lazy(() => import('web-app/react/routes/contract/contract'));
const Components = React.lazy(() => import('web-app/react/routes/components/components'));
const AccountRoutes = React.lazy(() => import('web-app/react/routes/account/account'));
const OpenId = React.lazy(() => import('web-app/react/routes/openid'));
const Login = React.lazy(() => import('web-app/react/routes/login'));
const CustomInquiryFormRoutes = React.lazy(
    () => import('web-app/react/routes/custom-inquiry-form/custom-inquiry-form'),
);
const Offer = React.lazy(() => import('web-app/react/routes/offer/offer'));
const EmailVerification = React.lazy(() => import('web-app/react/routes/email-verification/email-verification'));
const NewSiteRequest = React.lazy(() => import('web-app/react/routes/new-site-request/new-site-request'));
const NotFound = React.lazy(() => import('web-app/react/routes/not-found/not-found'));

const HAS_EVER_USED_AWS_LOCAL_STORAGE_KEY = 'hasEverUsedAWS';

const {Provider: NavigationContextProvider} = navigationContext;

const Application = () => {
    React.useEffect(() => {
        // This effect is only supposed to be run _once_. It performs some initial setup.
        // This effect was ported from the "activate" hook in the Ember route for application.

        if (PlatformHelper.isTablet()) {
            // Sets up listeners for (de)activating "focus mode" when writing posts in the news feed
            setupMenuHide();
        }

        // Some enterprise caching layers won't respect our no-caching policy leading users to receiving other users
        // data from caches that we don't control. This is an attempt at indicating to them that we're setting up a
        // session here that must not be cached.
        //
        // Cookies are not possible on the mobile app, so we only use this trick on web
        if (PlatformHelper.isWeb()) {
            document.cookie = `famly.session-marker=${generateUUID()}`;
        }

        // Sets installationId in Sentry scope so it will be attached in all future events
        configureSentryScope(scope => {
            scope.setTag('installationId', getInstallationId());
        });

        // Subscribe to REST errors and log out on SESSION_EXPIRED
        RESTClient.ErrorSubject.subscribe(error => {
            if (error.responseData && error.responseData.errorCode === APIErrorCode.SESSION_EXPIRED) {
                SessionHelpers.logOut(undefined, false, false);
            }
        });

        /**
         * Calling this function early in the app lifecycle is intended to ensure
         * the singleton subscription, that is supposed to send famlytics events to the API,
         * is initialised and ready for the app to use.
         */
        getConsumer();
    }, []);

    // As we update the "target API" (the German vs. the other server setup) dynamically (e.g. when the user logs in),
    // we need to be able to refresh the ApolloClient passed to ApolloProvider.
    // This is achieved by flipping the boolean in the `useState` - which forces React to re-render.
    // Notice that we do not set the key in the callback as ´getApolloClient()` gets that from APIHelper internally.
    const [, refreshApolloClient] = React.useState(false);
    React.useEffect(() => {
        APIHelper.subscribeToApiKey(apiKey => {
            // If the user ever touches the AWS servers (which is the case when the apiKey is not ".de")
            // we set a local storage flag that we can use later for loading scripts that shouldn't
            // be loaded for customors on the German server setup.
            if (
                getWhitelabel() !== Whitelabel.brighthorizons &&
                apiKey &&
                apiKey !== '.de' &&
                PlatformHelper.isMobileApp()
            ) {
                LocalStorageService.saveToLocalStorage(HAS_EVER_USED_AWS_LOCAL_STORAGE_KEY, true);
            }

            // In the mobile app, we only want to load the statuspage.io script and the killswitch if
            // we can see that the user _at some point_ connected to AWS.
            if (hasUsedAwsInMobileApp()) {
                loadStatusPage();
                Killswitch.startListeningForKillswitch();
            }

            refreshApolloClient(current => !current);
        });

        // On web, we only want to load the statuspage.io script and the killswitch if we're
        // on non-german servers.
        if (isUsingAWSOnWeb()) {
            loadStatusPage();
            Killswitch.startListeningForKillswitch();
        }
    }, [refreshApolloClient]);

    useSetupMobileApp();

    const [inc, forceRemount] = React.useReducer(s => s + 1, 1);

    const handleError = React.useCallback<Required<ErrorBoundaryPropsWithComponent>['onError']>((error, info) => {
        captureException(error, {extra: info});
    }, []);

    const [navigationBlockingPromptPayload, setNavigationBlockingPromptPayload] = React.useState<
        BlockNavigationPromptPayload | undefined
    >(undefined);

    const getUserConfirmationWhenBlockingNavigation = React.useCallback<
        NonNullable<InferProps<typeof Router>['getUserConfirmation']>
    >((payload, callback) => {
        const {dialogProps, previousHash, navigatingToPathname} = JSON.parse(
            payload,
        ) as BlockNavigationPromptPayload & {navigatingToPathname: string};

        // We only want to block navigation when the hash changes - not the query params.
        // So short-circuiting when that's the case.
        if (extractPathFromHash(previousHash) === extractPathFromHash(navigatingToPathname)) {
            callback(true);
            return;
        }

        // Otherwise, let the user decide whether to continue the navigation.
        showDialog(dialogProps).then(result => {
            callback(result === DialogChoice.OK);

            // At this point, the url bar in the browser has already changed.
            // If the user decided to cancel navigation, we'll need to reset it to the previous route.
            if (result === DialogChoice.CANCEL) {
                window.location.hash = previousHash;
            }
        });
    }, []);

    const fbLoginState = getFbLoginState();

    React.useEffect(() => {
        // If there's a Facebook login state, this redirects to the news feed tab in settings.
        // which will take care of finishing up the FB page setup
        if (
            fbLoginState &&
            fbLoginState.settingsItemId === 'NEWS_FEED' &&
            fbLoginState.provider === 'facebook-pages-integration'
        ) {
            window.location.hash = '#/account/settings?selectedTab=NEWS_FEED';
        }
    }, [fbLoginState]);

    return (
        <ErrorBoundary key={inc} FallbackComponent={ErrorFallback} onError={handleError} onReset={forceRemount}>
            <SWRConfig value={SWRConfigValue}>
                <ApolloProvider client={getApolloClient()}>
                    <Router getUserConfirmation={getUserConfirmationWhenBlockingNavigation}>
                        <NavigationBlockProvider
                            value={React.useMemo(() => ({setNavigationBlockingPromptPayload}), [])}
                        >
                            <ModalContext>
                                <InnerApplication />
                            </ModalContext>
                        </NavigationBlockProvider>
                        {navigationBlockingPromptPayload ? (
                            <Prompt
                                when={true}
                                message={navigatingTo => {
                                    // We check if the route we're navigating to is whitelisted (the Prompt shouldn't be shown).
                                    // kudos: https://v5.reactrouter.com/core/api/Prompt/message-func
                                    if (
                                        navigationBlockingPromptPayload.dialogProps.whiteList?.some(whiteListRoute =>
                                            navigatingTo.pathname.includes(whiteListRoute),
                                        )
                                    ) {
                                        return true;
                                    }

                                    // The result of this will be passed to the Router's getUserConfirmation function's first argument
                                    // As we have to return a string, we'll be using JSON.
                                    return JSON.stringify({
                                        dialogProps: navigationBlockingPromptPayload.dialogProps,
                                        navigatingToPathname: navigatingTo.pathname,
                                        previousHash: navigationBlockingPromptPayload.previousHash,
                                    });
                                }}
                            />
                        ) : null}
                    </Router>
                </ApolloProvider>
            </SWRConfig>
        </ErrorBoundary>
    );
};

const StyledCrashDialog = styled(DialogRenderer)`
    z-index: 999999;
` as typeof DialogRenderer;

const ErrorFallback: React.FC<React.PropsWithChildren<FallbackProps>> = props => {
    return (
        <StyledCrashDialog
            mounted
            title={i18next.t('errorBoundary.genericErrorTitle')}
            description={i18next.t('errorBoundary.genericErrorDescription')}
            handleChoice={choice => {
                switch (choice) {
                    case 'resetApp':
                        window.location.reload();
                        break;
                    case 'resetBoundary':
                        props.resetErrorBoundary();
                        break;
                    default:
                        return;
                }
            }}
            choices={[
                {
                    value: 'resetBoundary' as const,
                    appearance: ButtonAppearance.Default,
                    text: i18next.t('errorBoundary.genericErrorActionRecover'),
                },
                {
                    value: 'resetApp' as const,
                    appearance: ButtonAppearance.Danger,
                    text: i18next.t('errorBoundary.genericErrorActionReload'),
                },
            ]}
        />
    );
};

const SpinnerContainer = styled.div`
    padding-top: env(safe-area-inset-top);
`;

const InnerApplication: React.FC<React.PropsWithChildren<unknown>> = () => {
    const history = useHistory();

    const {
        generateUrl: emberGenerateUrl,
        replaceWith: emberReplaceWith,
        transitionTo: emberTransitionTo,
    } = React.useContext(navigationContext);

    const generateUrl = React.useMemo(
        () =>
            buildNavigationFunctionReplacement(
                'generateUrl',
                RouteConfig,
                hash => {
                    return `#${hash}`;
                },
                emberGenerateUrl,
            ),
        [emberGenerateUrl],
    );

    const replaceWith = React.useMemo(
        () =>
            buildNavigationFunctionReplacement(
                'replaceWith',
                RouteConfig,
                hash => {
                    history.replace(hash);
                    return hash;
                },
                emberReplaceWith,
            ),
        [emberReplaceWith, history],
    );

    const transitionTo = React.useMemo(
        () =>
            buildNavigationFunctionReplacement(
                'transitionTo',
                RouteConfig,
                hash => {
                    history.push(hash);
                    return hash;
                },
                emberTransitionTo,
            ),
        [emberTransitionTo, history],
    );

    const generateHash = useCallback(<T extends RouteConfigPath>(...args: PathGenerationArgs<T>) => {
        return `#${generatePath(args)}`;
    }, []);

    const transitionToPath = useCallback(
        <T extends RouteConfigPath>(...args: PathGenerationArgs<T>) => {
            const hash = generatePath(args);
            history.push(hash);
        },
        [history],
    );

    const replaceWithPath = useCallback(
        <T extends RouteConfigPath>(...args: PathGenerationArgs<T>) => {
            const hash = generatePath(args);
            history.replace(hash);
        },
        [history],
    );

    const navigationContextValue = React.useMemo(
        () => ({generateUrl, replaceWith, transitionTo, generateHash, transitionToPath, replaceWithPath}),
        [generateHash, generateUrl, replaceWith, replaceWithPath, transitionTo, transitionToPath],
    );

    React.useEffect(() => {
        /*
        |------------------------------------------------------------------------------
        | OIDC Callback handler
        |------------------------------------------------------------------------------
        |
        | When using callbacks in our OIDC flows (connecting accounts, authenticating, etc)
        | we have to "continue" the session and redirect. This takes care of that on web.
        | The mobile app is handled as part of the cordova plugin "InAppBrowser"'s lifecycle.
        */

        const searchParams = new URLSearchParams(window.location.search);

        // Ember is not able to pickup all the query parameters in the redirect url
        const searchParamsObject = LoginHelpers.paramsToObject(searchParams);

        // We expect `state` to be returned to us in window.location.search.
        // The mobile app transition is handled manually in the InAppBrowser lifecycle function - not here.
        if (!PlatformHelper.isMobileApp() && searchParamsObject?.state) {
            const stateFromLocalStorage = LocalStorageService.getFromLocalStorage(LoginHelpers.OPEN_ID_STATE_KEY_KEY);

            // If we can recognize `state` with what we have in local storage, we try to extract the data
            if (stateFromLocalStorage === searchParamsObject?.state) {
                const stateValue = LocalStorageService.getFromLocalStorage(
                    LoginHelpers.buildLocalStorageStateValueKey(stateFromLocalStorage),
                );

                const stateValueObject = JSON.parse(stateValue);

                const {route, ...restOfStateValue} = stateValueObject;

                // Transition to the route from the saved state. Incl both queryParams which contains the provider's auth code
                // along with the rest of the state.
                if (route) {
                    replaceWith(route, {
                        queryParams: {
                            ...searchParamsObject,
                            ...restOfStateValue,
                        },
                    });
                }
            }
        }

        // In any case, we want to clean up local storage - the (possible) redirect can only happen once.
        LoginHelpers.cleanupOpenIdState();
    }, [replaceWith]);

    React.useEffect(() => {
        const subscription = withAccessTokenResolved$
            .pipe(filter(status => status === 'access-token-invalid'))
            .subscribe(() => {
                /**
                 * If we're on an account route with no valid access token, redirect to login
                 */
                if (window.location.hash.startsWith('#/account')) {
                    navigationContextValue.transitionTo(RouteConfig.login.path);
                }
            });
        return () => {
            subscription.unsubscribe();
        };
    }, [history, navigationContextValue]);

    const navigationLinkId = useGetCurrentPageNavigationLinkId();

    const routeTitle = React.useMemo(() => {
        if (!navigationLinkId) {
            return undefined;
        }

        const navigationLinkDetails = getNavigationLinkDetails(navigationLinkId);
        return navigationLinkDetails.topBarTitle ?? navigationLinkDetails.title;
    }, [navigationLinkId]);

    React.useEffect(() => {
        const whitelabelName = getWhitelabelName();
        if (hasValue(routeTitle)) {
            document.title = `${whitelabelName} | ${routeTitle}`;
        } else {
            document.title = whitelabelName;
        }
    }, [routeTitle]);

    return (
        <NavigationContextProvider value={navigationContextValue}>
            <React.Suspense
                fallback={
                    <SpinnerContainer>
                        <Spinner centered margined />
                    </SpinnerContainer>
                }
            >
                <Switch>
                    <Route path={RouteConfig.welcome.path} component={Welcome} />
                    <Route path={RouteConfig.invitation.path} component={RoleInvitationRoute} />
                    <Route path={RouteConfig.signup.path} component={Signup} />
                    <Route path={RouteConfig.debugger.path} component={Debugger} />
                    <Route path={RouteConfig.icons.path} component={Icons} />
                    <Route path={RouteConfig.errorTest.path} component={ErrorTest} />
                    <Route path={RouteConfig.errorLog.path} component={ErrorLog} />
                    <Route path={RouteConfig.testLogin.path} component={TestLogin} />
                    <Route path={RouteConfig.inquiry.path} component={InquiryForm} />
                    <Route path={RouteConfig.contextChallenge.path} component={ContextChallenge} />
                    <Route path={RouteConfig.connectTokenSent.path} component={ConnectTokenSent} />
                    <Route path={RouteConfig.oneTimeLogin.path} component={OneTimeLogin} />
                    <Route path={RouteConfig.famlyLiveJoinMeeting.path} component={FamlyLiveJoinMeeting} />
                    <Route path={RouteConfig.famlyLiveMeetingLeft.path} component={MeetingLeft} />
                    <Route path={RouteConfig.forgotPassword.path} component={ForgotPassword} />
                    <Route path={RouteConfig.resetPassword.path} component={ResetPassword} />
                    <Route path={RouteConfig.confirmEmail.path} component={ConfirmEmail} />
                    <Route path={RouteConfig.newAccount.path} component={NewAccount} />
                    <Route path={RouteConfig.tooManyFailedLoginAttempts.path} component={TooManyFailedLoginAttempts} />
                    <Route path={RouteConfig.contract.path} component={Contract} />
                    <Route path={RouteConfig.components.path} component={Components} />
                    <Route path={RouteConfig.account.path} component={AccountRoutes} />
                    <Route path={RouteConfig.unavailable.path} component={ServersUnavailable} />
                    <Route path={RouteConfig.openid.path} component={OpenId} />
                    <Route path={RouteConfig.login.path} component={Login} />
                    <Route path={RouteConfig.customInquiryForm.path} component={CustomInquiryFormRoutes} />
                    <Route path={RouteConfig.offer.path} component={Offer} />
                    <Route path={RouteConfig.emailVerification.path} component={EmailVerification} />
                    <Route path={RouteConfig.newSiteRequest.path} component={NewSiteRequest} />
                    <Route path={RouteConfig.notFound.path} component={NotFound} />
                    {/* Redirect to /login and let the logic in that component redirect from there. */}
                    <Redirect path="/" exact to={RouteConfig.login.path} />
                </Switch>
            </React.Suspense>
        </NavigationContextProvider>
    );
};

const hasEverUsedAWS = () => {
    return LocalStorageService.getFromLocalStorage(HAS_EVER_USED_AWS_LOCAL_STORAGE_KEY) === true;
};

/*
|------------------------------------------------------------------------------
| Statuspage.io integration
|------------------------------------------------------------------------------
|
| Functions for loading a script that shows a message when we have open
| incidents on status.famly.co
|
| The script will be loaded conditionally, as we don't want to show customers
| on the German server setup when we're having problems with AWS.
*/

const loadStatusPage = () => {
    if (window.Cypress) {
        return;
    }

    loadScript({
        src: 'https://7k5pzm061fk5.statuspage.io/embed/script.js',
        scriptId: 'statuspageScript',
        documentNode: document.body,
    });
};

const hasUsedAwsInMobileApp = () => {
    // Bright horizons runs on a different server setup
    if (getWhitelabel() === Whitelabel.brighthorizons) {
        return false;
    }

    return PlatformHelper.isMobileApp() && hasEverUsedAWS();
};

const isUsingAWSOnWeb = () => {
    // Bright horizons runs on a different server setup
    if (getWhitelabel() === Whitelabel.brighthorizons) {
        return false;
    }

    if (!PlatformHelper.isWeb()) {
        return false;
    }

    const tld = window.location.origin.split('.').pop();

    return tld !== 'de';
};

/**
 * Note that this should be rendered in a div with class "application-view"
 */
const ApplicationShell = () => {
    return (
        <>
            {/* This component adds global styles our modals */}
            <LegacyModalStyle />

            <div className="application-root">
                <GlobalPrintHeader />
                <ApplicationProvider />
                <ReactSelectGlobalStyling />
                <GlobalEvent />
                <DialogComposer />
            </div>

            {/* This component adds global styles for <SplitNavigation /> */}
            <GlobalStyleSplitNavigation />
            {/* This component adds global styles tables */}
            <GlobalStyleLegacyTable />
            {/* This component adds global styles Select */}
            <GlobalStyleSelect />
            {/* This component adds global styles for tooltips */}
            <GlobalStyleTooltip />
            {/* This component adds global CSS variables */}
            <GlobalStyleCSSVariables />
            {/* This component adds global styles for hiding certain Beamer elements */}
            <GlobalBeamerStyles />
            {/* This component adds global styling for ModernFamly */}
            <GlobalStyleModernFamly />
        </>
    );
};

export default ApplicationShell;

/*
 * This component ensure nothing is rendered before both translations and access token
 * have been resolved.
 */
const ApplicationProvider = () => {
    const [isReady, setIsReady] = React.useState(false);

    React.useEffect(() => {
        // first() will automatically unsubscribe after recieving a value
        const subscription = withApplicationReady$.pipe(first()).subscribe(() => {
            setIsReady(true);
        });

        return () => {
            subscription.unsubscribe();
        };
    }, [setIsReady]);

    if (!isReady) {
        return null;
    }

    return <Application />;
};
